import React, { useState } from 'react';
import { Bell, MessageCircle, Calendar, FileText, CheckCircle, X } from 'lucide-react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useParentAssistance } from '../contexts/ParentAssistanceContext';

const SmartNotifications = () => {
  const { notifications } = useParentPortal();
  const { smartSuggestions } = useParentAssistance();
  const [showDropdown, setShowDropdown] = useState(false);

  // Combine notifications and smart suggestions
  const allNotifications = [
    ...smartSuggestions.map(suggestion => ({
      id: `suggestion-${suggestion.id}`,
      type: 'suggestion',
      priority: suggestion.priority,
      title: suggestion.title,
      message: suggestion.description,
      action: suggestion.action,
      route: suggestion.route,
      icon: suggestion.icon,
      timestamp: new Date() // Smart suggestions are always current
    })),
    ...(notifications || []).map(notif => ({
      id: notif.id,
      type: 'notification',
      priority: 'medium',
      title: notif.notification_type === 'item_updated' ? 'Document Updated' : 'New Activity',
      message: notif.message,
      timestamp: new Date(notif.created_at)
    }))
  ];

  // Sort by priority and timestamp
  const sortedNotifications = allNotifications.sort((a, b) => {
    const priorityOrder = { urgent: 3, high: 2, medium: 1, low: 0 };
    const aPriority = priorityOrder[a.priority] || 0;
    const bPriority = priorityOrder[b.priority] || 0;

    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  const unreadCount = sortedNotifications.filter(n =>
    n.type === 'suggestion' ||
    (n.type === 'notification' && !n.read)
  ).length;

  const getNotificationIcon = (type, iconName) => {
    if (type === 'suggestion') {
      switch (iconName) {
        case 'Brain':
          return <MessageCircle className="w-4 h-4" />;
        case 'FileText':
          return <FileText className="w-4 h-4" />;
        case 'Calendar':
          return <Calendar className="w-4 h-4" />;
        default:
          return <CheckCircle className="w-4 h-4" />;
      }
    }
    return <Bell className="w-4 h-4" />;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-coral-600 bg-coral-50 border-coral-200';
      case 'high':
        return 'text-magenta-600 bg-magenta-50 border-magenta-200';
      case 'medium':
        return 'text-azure-600 bg-azure-50 border-azure-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  return (
    <div className="relative">
      {/* Notification bell */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-magenta-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>

      {/* Notification dropdown */}
      {showDropdown && (
        <div className="absolute right-0 top-12 w-96 bg-white rounded-xl border border-slate-200 shadow-xl z-50">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Notifications & Suggestions</h3>
              <button
                onClick={() => setShowDropdown(false)}
                className="p-1 text-slate-500 hover:text-slate-700 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {sortedNotifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No new notifications</p>
                <p className="text-sm mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="space-y-1">
                {sortedNotifications.slice(0, 8).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-l-4 hover:bg-slate-50 cursor-pointer transition-colors ${getPriorityColor(notification.priority)}`}
                    onClick={() => {
                      if (notification.route) {
                        // Navigate to the suggested route
                        window.location.href = notification.route;
                      }
                      setShowDropdown(false);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getPriorityColor(notification.priority)}`}>
                        {getNotificationIcon(notification.type, notification.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-slate-900 text-sm">
                            {notification.title}
                          </h4>
                          <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                            {formatTimestamp(notification.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.action && notification.type === 'suggestion' && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full font-medium">
                              {notification.action} →
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {sortedNotifications.length > 8 && (
                  <div className="p-3 text-center border-t border-slate-200">
                    <button className="text-sm text-azure-600 hover:text-azure-700 font-medium">
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop for mobile */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default SmartNotifications;